import { useAtom } from 'jotai';
import { useLocalStorageState } from 'ahooks';
import { type CSSProperties, useCallback, useEffect, useState } from 'react';

import { useElementResize } from '@flintmint/web-shared/hooks/use-element-resize';

import { AIContextSection } from '@/components/ai-context-section';
import { AiSourcesSection } from '@/components/ai-sources-section';
import { ManageInstructionsModal } from '@/components/manage-instructions-modal';
import { type GroupedInstruction, InstructionsPanel } from '@/components/instructions-panel';

import { externalWorkspaceTabStateAtom } from '@/state';

import styles from './ai-context.module.scss';

export type OpenSection = 'context-items' | 'instructions' | null;

export const AiContext = (props: AiContextProps) => {
  const {
    instructionCount,
    groupedInstructions,
    documentInstructions,
    onInstructionDelete,
    onInstructionEnabledChange,
    onInstructionCreate,
    instructionsLoading,
    instructionEditorTestId = 'instruction-editor',
    view = 'editor',
  } = props;

  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [openSection, setOpenSection] = useLocalStorageState<OpenSection>('ai-context', {
    defaultValue: null,
  });

  const [externalWorkspaceTabState, setExternalWorkspaceTabState] = useAtom(externalWorkspaceTabStateAtom);

  const focusedBlockId = externalWorkspaceTabState?.focusedInstructionGroupId;

  const resetFocusedBlockId = () => setExternalWorkspaceTabState(null);

  const { ref, clientHeight } = useElementResize([openSection, instructionCount.total]);

  useEffect(() => {
    if (focusedBlockId) {
      setOpenSection('instructions');
    }
  }, [focusedBlockId, setOpenSection]);

  const handleSourcesOpenChange = useCallback(
    (isOpen: boolean) => setOpenSection(isOpen ? 'context-items' : null),
    [setOpenSection]
  );

  return (
    <div ref={ref} className={styles.aiContextShell}>
      <div className={styles.aiContext} style={{ '--max-height': `${clientHeight}px` } as CSSProperties}>
        {view !== 'block' && <AiSourcesSection onOpenChange={handleSourcesOpenChange} />}

        <AIContextSection
          dataCount={instructionCount}
          list={
            <InstructionsPanel
              readOnly
              view={view}
              focusedBlockId={focusedBlockId}
              enabled={!isInstructionsModalOpen}
              resetFocusedBlockId={resetFocusedBlockId}
              documentInstructions={documentInstructions}
              groupedInstructions={groupedInstructions}
              onInstructionDelete={onInstructionDelete}
              onInstructionEnabledChange={onInstructionEnabledChange}
              onAddInstruction={() => setIsInstructionsModalOpen(true)}
              isLoading={instructionsLoading}
              active={openSection === 'instructions'}
            />
          }
          title="Instructions"
          icon="3rd-bracket-bulk"
          open={openSection === 'instructions'}
          onOpenChange={(isOpen) => setOpenSection(isOpen ? 'instructions' : null)}
          onManage={() => setIsInstructionsModalOpen(true)}
          collapseTestId={`${view}-instructions-section-collapse`}
          manageTestId={`${view}-ai-context-manage-instructions`}
          listMaxHeightOffset={view === 'block' ? 64 : undefined}
        />

        <ManageInstructionsModal
          empty={!instructionCount.total}
          open={isInstructionsModalOpen}
          onInstructionCreate={onInstructionCreate}
          groupedInstructions={groupedInstructions}
          documentInstructions={documentInstructions}
          onInstructionDelete={onInstructionDelete}
          onInstructionEnabledChange={onInstructionEnabledChange}
          onClose={() => setIsInstructionsModalOpen(false)}
          editorTestId={instructionEditorTestId}
        />
      </div>
    </div>
  );
};

export interface AiContextProps {
  instructionCount: { enabled: number; total: number };
  groupedInstructions?: GroupedInstruction[];
  documentInstructions?: Record<string, boolean>;
  onInstructionDelete?: (instructionId: string) => void | Promise<void>;
  onInstructionEnabledChange: (instructionId: string, enabled: boolean, blockId?: string) => any;
  onInstructionCreate?: (instructionId: string) => void;
  instructionsLoading?: boolean;
  instructionEditorTestId?: string;
  view?: 'editor' | 'block';
}
